import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * ConfirmDialog Component
 * A reusable confirmation dialog to replace native window.confirm()
 * Follows the dark glassmorphism theme of the application
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Confirmation message
 * @param {function} props.onConfirm - Callback when user confirms
 * @param {function} props.onCancel - Callback when user cancels
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.severity - Severity level: 'warning', 'error', 'info' (default: 'warning')
 */
const ConfirmDialog = ({
    open,
    title = "Confirm Action",
    message,
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    severity = "warning"
}) => {
    const severityConfig = {
        warning: {
            color: '#F59E0B',
            icon: WarningAmberIcon,
            gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
        },
        error: {
            color: '#EF4444',
            icon: ErrorOutlineIcon,
            gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
        },
        info: {
            color: '#4CA1AF',
            icon: InfoOutlinedIcon,
            gradient: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)'
        }
    };

    const config = severityConfig[severity] || severityConfig.warning;
    const IconComponent = config.icon;

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                className: 'dark-confirm-dialog',
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.98) 0%, rgba(28, 40, 51, 0.98) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 161, 175, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(4px)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 2, pt: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `${config.color}20`,
                            border: `2px solid ${config.color}40`,
                            boxShadow: `0 0 20px ${config.color}30`,
                        }}
                    >
                        <IconComponent sx={{ color: config.color, fontSize: 28 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                            color: '#FFFFFF',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: '1rem',
                        lineHeight: 1.6
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    sx={{
                        borderColor: 'rgba(255, 255, 255, 0.35)',
                        color: '#E2E8F0',
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.6)',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: '#FFFFFF',
                        }
                    }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    sx={{
                        background: config.gradient,
                        color: '#FFFFFF',
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${config.color}40`,
                        '&:hover': {
                            background: config.gradient,
                            boxShadow: `0 6px 16px ${config.color}60`,
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
